import { useQuery } from "@tanstack/react-query";
// @ts-expect-error - `cookie-cutter` has no exported types
import Cookie from "cookie-cutter";
import { useAtomValue, useSetAtom } from "jotai";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import IconArrowRight from "@/icons/icon-arrow-right";
import IconCountry from "@/icons/icon-country";
import { countryDetectReadyState, sequentialLoadingState } from "~/atoms/sequential-loading";
import Dialog from "~/components/common/dialog";
import Icon from "~/components/common/icon";
import { useCountryDialog } from "~/contexts/country-dialog-context";
import { ApiConstants } from "~/shared/api/api-constants.class";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";
import $fetch from "~/utils/fetch-utils";
import { CultureLink } from "~/utils/locale-utils";

import styles from "./country-detect-modal.module.scss";

type Props = {};

interface countryResponse {
  country: string | null;
}

export default function CountryDetectModal(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const [isOpen, setOpen] = useState(false);
  const setCountryDetectLoaded = useSetAtom(sequentialLoadingState);
  const sequentialLoadingReady = useAtomValue(countryDetectReadyState);
  const { countries } = useCountryDialog();
  const allCountries = useMemo(() => countries.continents.flatMap((c) => c.countries), [countries]);

  const query = useQuery({
    queryKey: ["detectCountry"],
    staleTime: Infinity,
    cacheTime: Infinity,
    queryFn: () => $fetch(ApiConstants.endpoints.detectCountry).json() as Promise<countryResponse>,
    enabled: !!router.locale && router.locale !== "default",
  });

  useEffect(() => {
    if (query.data && sequentialLoadingReady) {
      const current = crossCountryUtils.getCurrentCountryCode(router).toLowerCase();
      const detected = query.data.country?.toLowerCase() ?? "gb";

      if (current !== detected) {
        const cookie = Cookie.get("country-detect");

        if (!cookie || cookie !== `${current}-${detected}`) {
          setOpen(true);
        }
      }
      setCountryDetectLoaded((current) => ({ ...current, countryDetect: true }));
    }
  }, [sequentialLoadingReady, query.data, router, setCountryDetectLoaded]);

  useEffect(() => {
    if (isOpen) {
      const didomiHost = document.getElementById("didomi-host");
      if (didomiHost) {
        didomiHost.ariaHidden = "true";
      }
    }
  }, [isOpen, sequentialLoadingReady]);

  const current = crossCountryUtils.getCurrentCountryCode(router).toLowerCase();
  const currentCountry = allCountries.find((c) => c.countryCode.toLowerCase() === current.toLowerCase());

  if (!currentCountry) {
    return null;
  }

  if (!query.data) {
    return null;
  }

  const detected = query.data.country?.toLowerCase() ?? "gb";
  const detectedCountry =
    allCountries.find((c) => c.countryCode.toLowerCase() === detected.toLowerCase()) ||
    allCountries.find((c) => c.countryCode.toLowerCase() === "gb")!;

  const defaultCulture = detectedCountry.cultures.find((c) => c.default) ?? detectedCountry.cultures[0];

  function stayInCurrentCountry() {
    Cookie.set("country-detect", `${current}-${detected}`, {
      path: "/",
    });

    setOpen(false);
    const didomiHost = document.getElementById("didomi-host");
    if (didomiHost) {
      didomiHost.ariaHidden = null;
    }
    document.getElementById(Constants.USABLENET_LINK_KEY)?.focus();
  }

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <div className={styles.wrapperTitle}>
          <Icon icon={IconCountry} aria-hidden />
          <span className={styles.title}>{t("generic.check_your_country")}</span>
        </div>
      }
      onClose={() => stayInCurrentCountry()}
      customStyles={{ wrapper: styles.countryWrapperDialog, overlay: styles.overlay }}
      shouldCloseOnOverlayClick={false}
      shouldFocusAfterRender={false}
    >
      <div>
        <p className={styles.updateCountry}>
          {t.rich("generic.update_your_country", {
            country: detectedCountry.name,
            strong: (chunks) => <strong className={styles.highlightedCountry}>{chunks}</strong>,
            br: (chunks) => <br aria-hidden />,
          })}
        </p>
        <CultureLink
          country={detectedCountry}
          culture={defaultCulture}
          className={styles.wrapperCTA}
          aria-label={
            t("generic.yes") +
            " " +
            t("generic.go_to_country_website", {
              country: detectedCountry.name,
            })
          }
        >
          <Image
            unoptimized
            width={28}
            height={28}
            alt=""
            aria-hidden
            src={`/next/assets/flags/${detectedCountry.countryCode}.svg`}
            className={styles.flag}
          />
          <span className={styles.wrapperAnswer} aria-hidden>
            <span className={styles.answer}>{t("generic.yes")}</span>
            <span className={styles.paragraph}>
              {t("generic.go_to_country_website", {
                country: detectedCountry.name,
              })}
            </span>
          </span>
          <Icon icon={IconArrowRight} width={16} height={16} className={styles.icon} aria-hidden />
        </CultureLink>
        <button
          onClick={() => stayInCurrentCountry()}
          className={styles.wrapperCTA}
          aria-label={t("generic.no") + " " + t("generic.stay_in_country", { country: currentCountry.name })}
        >
          <Image
            unoptimized
            width={28}
            height={28}
            alt=""
            aria-hidden
            src={`/next/assets/flags/${currentCountry.countryCode}.svg`}
            className={styles.flag}
          />
          <span className={styles.wrapperAnswer} aria-hidden>
            <span className={styles.answer}>{t("generic.no")}</span>
            <span className={styles.paragraph}>{t("generic.stay_in_country", { country: currentCountry.name })}</span>
          </span>
          <Icon icon={IconArrowRight} width={16} height={16} className={styles.icon} aria-hidden />
        </button>
      </div>
    </Dialog>
  );
}
